import { normalizeBusinessDatePayload, synchronizeAppClock } from "@/lib/date-utils";
import { reportSystemError } from "@/lib/monitoring";

export type ApiListResponse<T> = { data: T[] };
export type ApiItemResponse<T> = { data: T };

const jsonHeaders = { "Content-Type": "application/json", "Cache-Control": "no-cache" };
const TENANT_WIDE_ENTITIES = new Set([
  "clientes_saas",
  "clinicas",
  "usuarios_clinica",
  "modulos_saas",
  "modulos_cliente",
  "modelos_atestado",
  "modelos_contrato",
]);

export function activeClinicaId() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("odonto.activeClinicaId") || "";
}

export function activeClienteId() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("odonto.activeClienteId") || "";
}

function apiUrl(entity: string, id?: string, fresh = false) {
  const params = new URLSearchParams({ entity });
  if (id) params.set("id", id);
  const clinicaId = activeClinicaId();
  if (clinicaId && !TENANT_WIDE_ENTITIES.has(entity)) {
    params.set("clinica_id", clinicaId);
  }
  const clienteId = activeClienteId();
  if (clienteId) {
    params.set("cliente_id", clienteId);
  }
  if (fresh) params.set("_fresh", String(Date.now()));
  return `/api/data.php?${params.toString()}`;
}

async function parseResponse<T>(response: Response): Promise<T> {
  synchronizeAppClock(response.headers.get("Date"));
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.error) {
    void reportSystemError(data?.error || `HTTP ${response.status}`, { source: "api", status: response.status, endpoint: response.url });
    throw new Error(data?.error || "Erro na API local");
  }
  return data as T;
}

export async function apiList<T>(entity: string): Promise<T[]> {
  const response = await fetch(apiUrl(entity, undefined, true), {
    credentials: "include",
    cache: "no-store",
  });
  const payload = await parseResponse<ApiListResponse<T>>(response);
  return normalizeBusinessDatePayload(payload.data || []);
}

export async function apiListForClinic<T>(entity: string, clinicaId: string): Promise<T[]> {
  const params = new URLSearchParams({ entity, clinica_id: clinicaId, _fresh: String(Date.now()) });
  const clienteId = activeClienteId();
  if (clienteId) params.set("cliente_id", clienteId);
  const response = await fetch(`/api/data.php?${params.toString()}`, {
    credentials: "include",
    cache: "no-store",
  });
  const payload = await parseResponse<ApiListResponse<T>>(response);
  return normalizeBusinessDatePayload(payload.data || []);
}

export async function apiCreate<T>(entity: string, item: Record<string, unknown>): Promise<T> {
  const response = await fetch(apiUrl(entity), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: jsonHeaders,
    body: JSON.stringify(normalizeBusinessDatePayload(item)),
  });
  const payload = await parseResponse<ApiItemResponse<T>>(response);
  return normalizeBusinessDatePayload(payload.data);
}

export async function apiUpdate<T>(entity: string, id: string, item: Record<string, unknown>): Promise<T> {
  const response = await fetch(apiUrl(entity, id), {
    method: "PUT",
    credentials: "include",
    cache: "no-store",
    headers: jsonHeaders,
    body: JSON.stringify(normalizeBusinessDatePayload(item)),
  });
  const payload = await parseResponse<ApiItemResponse<T>>(response);
  return normalizeBusinessDatePayload(payload.data);
}

export async function apiDelete(entity: string, id: string): Promise<void> {
  const response = await fetch(apiUrl(entity, id), {
    method: "DELETE",
    credentials: "include",
    cache: "no-store",
  });
  await parseResponse<{ ok: boolean }>(response);
}

export async function apiTransfer(entity: string, id: string, targetClinicaId: string): Promise<void> {
  const params = new URLSearchParams({
    entity,
    action: "transfer",
    id,
    target_clinica_id: targetClinicaId,
  });
  const response = await fetch(`/api/data.php?${params.toString()}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: jsonHeaders,
    body: JSON.stringify({}),
  });
  await parseResponse<{ ok: boolean }>(response);
}

export async function apiEmitReceipt<T>(documento: Record<string, unknown>, movimentacao: Record<string, unknown>, movimentacaoExistenteId?: string): Promise<T> {
  const params = new URLSearchParams({ entity: "documentos_emitidos", action: "emit_receipt" });
  const clinicId = activeClinicaId();
  if (clinicId) params.set("clinica_id", clinicId);
  const response = await fetch(`/api/data.php?${params.toString()}`, {
    method: "POST", credentials: "include", cache: "no-store", headers: jsonHeaders,
    body: JSON.stringify(normalizeBusinessDatePayload({ documento, movimentacao, movimentacao_existente_id: movimentacaoExistenteId || "" })),
  });
  return (await parseResponse<ApiItemResponse<T>>(response)).data;
}

export function withFallbackId<T extends Record<string, any>>(item: T): T {
  return {
    id: item.id || String(Date.now()),
    created_at: item.created_at || new Date().toISOString(),
    updated_at: item.updated_at || new Date().toISOString(),
    ...item,
  };
}
